import { useState, useCallback } from 'react';
import type { StepDefinition, FieldDefinition } from '@klaro7/shared-types';
import { Button } from '@klaro7/ui';

import { FieldEditor } from './FieldEditor';
import { DynamicStepRenderer } from './DynamicStepRenderer';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StepDefinitionEditorProps {
  stepDef: StepDefinition;
  onSave: (updated: StepDefinition) => void;
  onCancel: () => void;
  isSaving: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

function generateFieldKey(fields: FieldDefinition[]): string {
  let counter = fields.length + 1;
  let key = `nuovoCampo${counter}`;
  const existingKeys = new Set(fields.map((f) => f.key));
  while (existingKeys.has(key)) {
    counter++;
    key = `nuovoCampo${counter}`;
  }
  return key;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StepDefinitionEditor({ stepDef, onSave, onCancel, isSaving }: StepDefinitionEditorProps) {
  const [working, setWorking] = useState<StepDefinition>(() => deepClone(stepDef));
  const [previewData, setPreviewData] = useState<Record<string, unknown>>({});
  const [previewErrors, setPreviewErrors] = useState<Record<string, string>>({});

  // --- Field list operations ---

  const updateField = useCallback((index: number, updated: FieldDefinition) => {
    setWorking((prev) => {
      const fields = [...prev.fields];
      fields[index] = updated;
      return { ...prev, fields };
    });
  }, []);

  const removeField = useCallback((index: number) => {
    setWorking((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  }, []);

  const moveField = useCallback((index: number, direction: -1 | 1) => {
    setWorking((prev) => {
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= prev.fields.length) return prev;
      const fields = [...prev.fields];
      const temp = fields[index]!;
      fields[index] = fields[newIndex]!;
      fields[newIndex] = temp;
      return { ...prev, fields };
    });
  }, []);

  const addField = useCallback(() => {
    setWorking((prev) => ({
      ...prev,
      fields: [
        ...prev.fields,
        {
          key: generateFieldKey(prev.fields),
          label: 'Nuovo campo',
          type: 'text',
          required: false,
        },
      ],
    }));
  }, []);

  // --- Preview handlers ---

  const handlePreviewChange = useCallback((field: string, value: unknown) => {
    setPreviewData((prev) => ({ ...prev, [field]: value }));
    setPreviewErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Actions ---

  const handleSave = () => {
    onSave(working);
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header bar */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-neutral-200 bg-white flex-shrink-0">
        <h2 className="text-lg font-bold text-neutral-900 flex-1">
          Step {working.step} — {working.name}
        </h2>
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          Annulla
        </Button>
        <Button onClick={handleSave} loading={isSaving}>
          Salva
        </Button>
      </div>

      {/* Split content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Editor */}
        <div className="w-1/2 overflow-y-auto border-r border-neutral-200 p-6 space-y-6">
          {/* Step name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Nome step</label>
            <input
              type="text"
              value={working.name}
              onChange={(e) => setWorking((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Step description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Descrizione step</label>
            <input
              type="text"
              value={working.description}
              onChange={(e) => setWorking((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Fields list */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-neutral-700">
              Campi ({working.fields.length})
            </h3>

            {working.fields.map((field, index) => (
              <FieldEditor
                key={`${field.key}-${index}`}
                field={field}
                index={index}
                totalFields={working.fields.length}
                onChange={(updated) => updateField(index, updated)}
                onRemove={() => removeField(index)}
                onMoveUp={() => moveField(index, -1)}
                onMoveDown={() => moveField(index, 1)}
              />
            ))}

            <button
              type="button"
              onClick={addField}
              className="w-full py-3 rounded-lg border-2 border-dashed border-neutral-300 text-sm font-medium text-neutral-500 hover:border-primary-400 hover:text-primary-600 transition-colors"
            >
              + Aggiungi campo
            </button>
          </div>
        </div>

        {/* Right: Preview */}
        <div className="w-1/2 overflow-y-auto bg-neutral-50 p-6">
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-4">
            Anteprima
          </p>
          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <DynamicStepRenderer
              stepDef={working}
              data={previewData}
              errors={previewErrors}
              onChange={handlePreviewChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
